import React, { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import ServicePageLayout from "../components/ServicePageLayout";
import ConfirmSheet from "../components/ConfirmSheet";
import { useGuestSession } from "contexts/GuestSessionContext";
import { useNavigate } from "react-router-dom";
import { withGuestSearch } from "data/guestPortalDefaults";
import { LUXURY } from "data/guestLuxuryTheme";

const HelpPage = () => {
  const navigate = useNavigate();
  const session = useGuestSession();
  const stay = session.portalConfig.stay;
  const frontDesk = stay.frontDeskPhone || session.company?.phone || "";
  const emergency = stay.emergencyPhone || "";
  const [confirm, setConfirm] = useState(null);

  const callWaiter = () => {
    const created = session.createRequest({
      type: "help",
      title: session.isRoom ? "Please assist this room" : "Please send a waiter",
      details: session.locationLabel,
      etaMinutes: 5,
    });
    setConfirm({
      title: session.isRoom ? "Front desk notified" : "Waiter requested",
      message: `We’ll come to ${session.locationLabel}. Usually about 5 min.`,
      id: created.id,
    });
  };

  return (
    <ServicePageLayout
      title="Help"
      subtitle="Call us, or send a quiet request if you don’t want to pick up the phone."
      illustration="help"
    >
      <Stack spacing={1.5}>
        <Button
          variant="contained"
          startIcon={<RoomServiceIcon />}
          onClick={callWaiter}
          sx={{
            minHeight: 50,
            fontWeight: 800,
            background: LUXURY.ctaGradient,
            color: LUXURY.forestDeep,
            borderRadius: "14px",
            whiteSpace: "normal",
            lineHeight: 1.3,
            py: 1.1,
            boxShadow: LUXURY.ctaShadow,
            "&:hover": { background: LUXURY.ctaGradientHover },
          }}
        >
          {session.isRoom ? "Please assist my room" : "Call a waiter"}
        </Button>

        {frontDesk ? (
          <Button
            href={`tel:${frontDesk}`}
            startIcon={<PhoneIcon />}
            variant="outlined"
            sx={{
              minHeight: 50,
              fontWeight: 800,
              borderRadius: "14px",
              whiteSpace: "normal",
              lineHeight: 1.3,
              py: 1.1,
              borderColor: LUXURY.goldLine,
              color: LUXURY.gold,
            }}
          >
            Front desk · {frontDesk}
          </Button>
        ) : null}

        {emergency ? (
          <Button
            href={`tel:${emergency}`}
            startIcon={<PhoneIcon />}
            color="error"
            variant="outlined"
            sx={{ minHeight: 50, fontWeight: 800, borderRadius: "14px", whiteSpace: "normal", lineHeight: 1.3, py: 1.1 }}
          >
            Emergency · {emergency}
          </Button>
        ) : null}

        <Box sx={{ p: 2, borderRadius: "18px", bgcolor: LUXURY.card, border: `1px solid ${LUXURY.cardBorder}` }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3, wordBreak: "break-word", color: LUXURY.ink }}>If you can’t reach anyone</Typography>
          <Typography sx={{ color: LUXURY.muted, fontSize: "0.9rem", lineHeight: 1.5, wordBreak: "break-word" }}>
            Use My requests to see what you already sent. Food orders can also be tracked from there.
          </Typography>
        </Box>
      </Stack>

      <ConfirmSheet
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        onClose={() => setConfirm(null)}
        onViewRequests={() => {
          setConfirm(null);
          navigate(withGuestSearch("/guest/requests", session.guestSearch));
        }}
      />
    </ServicePageLayout>
  );
};

export default HelpPage;
